import { setUser } from "@sentry/react";
import { createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { getAdminDashboardStats } from "@/lib/client/admin";
import { signOut } from "@/lib/client/auth";
import { AdminLayoutPage } from "@/features/admin/admin-layout-page";
import type { UserRole } from "@/constants";
import type { UserGrowthRangeDays } from "@/features/admin/admin-dashboard/types";

export const Route = createFileRoute("/admin")({
  staticData: {
    title: "后台首页",
  },
  head: () => ({
    meta: [{ name: "robots", content: "noindex,nofollow" }],
  }),
  component: RouteComponent,
  beforeLoad({ context }) {
    if (!context.authed) {
      throw redirect({ to: "/auth", replace: true, statusCode: 302 });
    }
    if (!context.roles.includes("admin")) {
      throw redirect({ to: "/", replace: true, statusCode: 302 });
    }
  },
});

function RouteComponent() {
  const [userGrowthRangeDays, setUserGrowthRangeDays] = useState<UserGrowthRangeDays>(7);
  const { user, queryClient, roles } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isAdminHome = pathname === "/admin" || pathname === "/admin/";
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "dashboard", "stats", { userGrowthRangeDays }],
    queryFn: () => getAdminDashboardStats({ userGrowthRangeDays }),
  });

  const navigateToAuth = async () => {
    await navigate({ to: "/auth", replace: true });
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
    queryClient.clear();
    await navigateToAuth();
  };

  const normalizedRoles = roles.filter(
    (role: string): role is UserRole => role === "user" || role === "reviewer" || role === "admin",
  );

  return (
    <AdminLayoutPage
      isAdminHome={isAdminHome}
      isLoading={isLoading}
      roles={normalizedRoles}
      userName={user?.name}
      dashboardData={data}
      userGrowthRangeDays={userGrowthRangeDays}
      onUserGrowthRangeChange={setUserGrowthRangeDays}
      onSignOut={handleSignOut}
    />
  );
}
